/**
 * @module components/Settings/NotificationsTab
 * @description Pestaña de preferencias de notificaciones del usuario.
 *
 * Permite configurar: notificaciones por email (sí/no), frecuencia del digest
 * (inmediata, diaria, semanal), notificaciones in-app y alertas del navegador.
 * Las preferencias se guardan en `users.metadata.notifications`.
 *
 * @see SettingsModal que centraliza el guardado
 * @see DataContext.notifications para el estado de notificaciones in-app
 */
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Bell, Mail, Monitor, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export const NotificationsTab = forwardRef<{ saveChanges: () => Promise<void> }>((props, ref) => {
  const { user, updateMetadata } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(
    user?.metadata?.notifications?.emailNotifications ?? true
  );
  const [emailFrequency, setEmailFrequency] = useState(
    user?.metadata?.notifications?.emailFrequency || 'daily'
  );
  const [browserNotifications, setBrowserNotifications] = useState(
    user?.metadata?.notifications?.browserNotifications ?? false
  );
  const [notifyTaskAssigned, setNotifyTaskAssigned] = useState(
    user?.metadata?.notifications?.notifyTaskAssigned ?? true
  );
  const [notifyControlCompleted, setNotifyControlCompleted] = useState(
    user?.metadata?.notifications?.notifyControlCompleted ?? true
  );
  const [notifyDueDateApproaching, setNotifyDueDateApproaching] = useState(
    user?.metadata?.notifications?.notifyDueDateApproaching ?? true
  );
  const [notifyEvidenceUploaded, setNotifyEvidenceUploaded] = useState(
    user?.metadata?.notifications?.notifyEvidenceUploaded ?? false
  );
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSaveNotifications = async () => {
    try {
      await updateMetadata({
        notifications: {
          emailNotifications,
          emailFrequency,
          browserNotifications,
          notifyTaskAssigned,
          notifyControlCompleted,
          notifyDueDateApproaching,
          notifyEvidenceUploaded
        }
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error guardando configuración de notificaciones:', err);
      throw err;
    }
  };

  // Expose saveChanges method to parent via ref
  useImperativeHandle(ref, () => ({
    saveChanges: handleSaveNotifications
  }));

  const toggleSwitch = (value: boolean, setter: (val: boolean) => void) => {
    return (
      <button
        onClick={() => setter(!value)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          value ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            value ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Preferencias de Notificaciones
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Configura cómo y cuándo quieres recibir notificaciones
        </p>
      </div>

      {/* Email notifications */}
      <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h4 className="font-medium text-gray-900 dark:text-white">
                Notificaciones por correo
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Recibe resúmenes diarios de actividad por email
            </p>
          </div>
          {toggleSwitch(emailNotifications, setEmailNotifications)}
        </div>
        {emailNotifications && (
          <div className="ml-7 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              Frecuencia de resúmenes por email:
            </p>
            <select
              value={emailFrequency}
              onChange={(e) => setEmailFrequency(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="immediate">Inmediatamente</option>
              <option value="daily">Resumen diario</option>
              <option value="weekly">Resumen semanal</option>
              <option value="critical">Solo notificaciones críticas</option>
            </select>
          </div>
        )}
      </div>

      {/* Browser notifications */}
      <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h4 className="font-medium text-gray-900 dark:text-white">
                Notificaciones del navegador
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Recibe notificaciones push en tu navegador
            </p>
          </div>
          {toggleSwitch(browserNotifications, setBrowserNotifications)}
        </div>
        {browserNotifications && (
          <div className="ml-7 mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm text-yellow-800 dark:text-yellow-300">
            Tu navegador debe otorgar permisos para mostrar notificaciones
          </div>
        )}
      </div>

      {/* Event notifications */}
      <div>
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h4 className="font-medium text-gray-900 dark:text-white">
              Notificarme cuando...
            </h4>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Selecciona los eventos que quieres que te notifiquen
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Me asignan una tarea
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Cuando se te asigna un control o tarea nueva
                </p>
              </div>
            </div>
            {toggleSwitch(notifyTaskAssigned, setNotifyTaskAssigned)}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Se completa un control
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Cuando un control es marcado como completado
                </p>
              </div>
            </div>
            {toggleSwitch(notifyControlCompleted, setNotifyControlCompleted)}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <Bell className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Se acerca una fecha límite
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Recordatorio 3 días antes del vencimiento
                </p>
              </div>
            </div>
            {toggleSwitch(notifyDueDateApproaching, setNotifyDueDateApproaching)}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Mail className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Se sube nueva evidencia
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Cuando alguien agrega evidencia a un control
                </p>
              </div>
            </div>
            {toggleSwitch(notifyEvidenceUploaded, setNotifyEvidenceUploaded)}
          </div>
        </div>
      </div>

      {saveSuccess && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-sm font-medium text-green-900 dark:text-green-100">
            ✓ Configuración de notificaciones guardada exitosamente
          </p>
        </div>
      )}
    </div>
  );
});
